import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { Clear } from '@material-ui/icons';
import { IconButton } from '@material-ui/core';
import Typography from 'Components/UI/Typography';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import GrowingProgressBar from 'Components/UI/GrowingProgressBar';
import BestNextStepCheckbox from 'Components/UI/BestNextStepCheckbox';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '250px',
    backgroundColor: theme.palette.facet.primaryBlue,
    padding: theme.spacing(1),
    cursor: 'pointer',
  },
  backgroundSelected: {
    backgroundColor: theme.palette.facet.accentBlue7,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.common.white,
  },
  cardTitleLeft: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    width: '175px',
    marginRight: theme.spacing(1),
  },
  cardTitleText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h6.fontSize,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  completedColor: {
    color: theme.palette.facet.accentBlue8,
  },
}));

export default function MyGoalsItem({
  goal, selected, onDelete, updateGoalCollection,
}) {
  const classes = useStyles();
  const {
    rootNodeProps,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent({ nodeName: 'Goal Sidebar Item' });

  const percent = goal.attr('percentComplete');
  const completed = goal.attr('completed') === 'true';

  const goalLabels = goal.meta().field('type').options().map();
  const goalName = goal.attr('name') || goalLabels[goal.attr('type')]?.attr('prompt');
  const goalType = goal.selectedOptions('type', 'prompt')[0];

  const deleteGoal = async () => {
    try {
      await goal.link('self').Delete();
      onDelete();
      logResourceEventSuccess('Client Goals Page: Clicked Delete on Goal', goal);
    } catch (e) {
      displayErrorMessage(e, 'Could not delete goal');
      logResourceEventFailureWithError('Client Goals Page: Clicked Delete on Goal', goal, e);
    }
  };

  return (
    <div
      {...rootNodeProps}
      className={classNames(classes.container, {
        [classes.backgroundSelected]: selected,
      })}
    >
      <div className={classes.cardHeader}>
        <span className={classes.cardTitleLeft}>
          <span className={classNames(classes.cardTitleText, {
            [classes.completedColor]: completed,
          })}
          >
            {goalName}
          </span>
        </span>
        {completed && onDelete
          ? (
            <IconButton size="small" onClick={deleteGoal}>
              <Clear className={classes.completedColor} />
            </IconButton>
          )
          : (
            <BestNextStepCheckbox goalResource={goal} updateGoalCollection={updateGoalCollection} />
          )
        }
      </div>
      <Typography
        color="accentBlue9"
        noWrap
      >
        {goalType}
      </Typography>
      <GrowingProgressBar progress={percent} completed={completed} />
    </div>
  );
}

MyGoalsItem.defaultProps = {
  selected: false,
  onDelete: null,
};

MyGoalsItem.propTypes = {
  goal: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  updateGoalCollection: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};
